import { useEffect, useMemo, useState } from 'react';
import { t } from '../i18n/idioma';
import type { CargaTela } from '../world/useWorld';
import { SimboloMarca } from './marca/SimboloMarca';
import '../styles/v3-carregamento.css';

/**
 * A tela de carregamento do mundo (techspec-loading; protótipo em docs/prototipos/loading-prototipo.html). Cobre a
 * tela preta da entrada, a viagem entre cidades, a reconexão e a falha da entrada. Some com fade quando o motor
 * avisa que o boneco está desenhável.
 * - Tela cheia: entrar no mundo e a falha. Só a área do mapa (o topo fica): viajar e reconectar.
 * - As etapas seguem o motor (`CargaMundo`); `conectar` e `reconectar` saem do hook.
 */

const DICAS = 6;
const SAIDA_MS = 450;
type Etapa = CargaTela['etapa'];

const PASSOS_ENTRAR: readonly Etapa[] = ['conectar', 'sala', 'desenhar'];
const PASSOS_VIAGEM: readonly Etapa[] = ['mapa', 'desenhar'];

export function TelaCarregamento({ carga, falha, shard, onTentar, onVoltar }: {
  carga: CargaTela;
  falha: string | null;
  shard?: string;
  onTentar(): void;
  onVoltar(): void;
}) {
  const ativa = !!falha || carga.etapa !== 'pronto';
  const [montada, setMontada] = useState(ativa);
  useEffect(() => {
    if (ativa) { setMontada(true); return; }
    const id = window.setTimeout(() => setMontada(false), SAIDA_MS);
    return () => window.clearTimeout(id);
  }, [ativa]);

  const [dica, setDica] = useState(() => Math.floor(Math.random() * DICAS));
  useEffect(() => {
    if (!montada) return;
    const id = window.setInterval(() => setDica(d => (d + 1) % DICAS), 6000);
    return () => window.clearInterval(id);
  }, [montada]);

  const carta = useMemo(cartaNautica, []);
  if (!montada) return null;

  const reconectando = carga.etapa === 'reconectar';
  const viagem = carga.viagem && !falha;
  const soMapa = !falha && (viagem || reconectando);
  const passos = viagem ? PASSOS_VIAGEM : PASSOS_ENTRAR;
  const atual = carga.etapa === 'pronto' ? passos.length : Math.max(0, passos.indexOf(carga.etapa));
  const kicker = falha ? t('carregamento.algoErrado')
    : reconectando ? t('carregamento.conexaoCaiu')
    : viagem ? t('carregamento.viajandoPara') : t('carregamento.entrandoEm');
  const sub = falha ? t('carregamento.sub.falha') : t(`carregamento.sub.${carga.etapa}`);
  const rotulo = (p: Etapa) => (p === 'mapa' && viagem && carga.de
    ? t('carregamento.passo.sair', { cidade: carga.de.toUpperCase() })
    : t(`carregamento.passo.${p}`));

  return (
    <section
      className={`ld${soMapa ? ' ld--mapa' : ''}${ativa ? '' : ' ld--saindo'}${falha ? ' ld--falhou' : ''}`}
      role={falha ? 'alert' : 'status'}
      aria-live="polite"
      aria-label={t('carregamento.aria', { cidade: carga.cidade })}
    >
      <svg className="ld__carta" viewBox="0 0 1600 1000" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        {carta.grade.map((l, i) => <line key={`g${i}`} {...l} className="ld__grade" />)}
        {carta.rumos.map((l, i) => <line key={`r${i}`} {...l} className="ld__rumo" />)}
        {carta.curvas.map((d, i) => <path key={`c${i}`} d={d} className="ld__curva" />)}
      </svg>
      <div className="ld__vinheta" />
      <div className="ld__moldura" />

      <div className="ld__in">
        <div className="ld__rosa" aria-hidden="true">
          <div className="ld__brilho" />
          <div className="ld__anel ld__anel--giro" />
          <div className="ld__anel" />
          <div className="ld__anel ld__anel--arco" />
          <SimboloMarca className="ld__simbolo" />
        </div>
        <div className="ld__kicker">{kicker}</div>
        <h1 className="ld__titulo">
          {reconectando && !falha ? t('carregamento.reconectando') : <em>{carga.cidade}</em>}
        </h1>
        {viagem && carga.de && (
          <div className="ld__rota"><span>{carga.de}</span><span className="ld__trilha"><i /></span><span>{carga.cidade}</span></div>
        )}
        <p className="ld__sub">{sub}</p>

        {falha ? (
          <div className="ld__falha">
            <div>{falha}</div>
            <div className="ld__botoes">
              <button type="button" className="ld__btn ld__btn--ouro" onClick={onTentar}>{t('mundo.entrarDeNovo')}</button>
              <button type="button" className="ld__btn" onClick={onVoltar}>{t('mundo.voltarPersonagens')}</button>
            </div>
            <div className="ld__det">{t('carregamento.falhaAjuda')}</div>
          </div>
        ) : (
          <div className="ld__progresso">
            <div className={`ld__barra${reconectando ? ' ld__barra--indet' : ''}`}>
              <i style={reconectando ? undefined : { width: `${Math.min(100, ((atual + (carga.etapa === 'pronto' ? 0 : 0.5)) / passos.length) * 100)}%` }} />
            </div>
            {!reconectando && (
              <div className="ld__passos">
                {passos.map((p, i) => (
                  <span key={p} className={i < atual ? 'feito' : i === atual ? 'agora' : undefined}>{rotulo(p)}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ld__dica">
        <b>{t('carregamento.dicaTitulo')}</b>
        <p key={dica}>{t(`carregamento.dica.${dica + 1}`)}</p>
      </div>
      {shard && <div className="ld__ver">{t('carregamento.rodape', { shard: shard.toUpperCase() })}</div>}
    </section>
  );
}

type Linha = { x1: number; y1: number; x2: number; y2: number };

/** A carta náutica do fundo: grade, linhas de rumo e curvas de nível de ilhas, desenhada aqui (sem arte de terceiros). */
function cartaNautica(): { grade: Linha[]; rumos: Linha[]; curvas: string[] } {
  const grade: Linha[] = [];
  for (let x = 0; x <= 1600; x += 160) grade.push({ x1: x, y1: 0, x2: x, y2: 1000 });
  for (let y = 0; y <= 1000; y += 160) grade.push({ x1: 0, y1: y, x2: 1600, y2: y });
  const rumos: Linha[] = Array.from({ length: 16 }, (_, i) => ({
    x1: 800, y1: 500,
    x2: Math.round(800 + 1100 * Math.cos((i * Math.PI) / 8)),
    y2: Math.round(500 + 1100 * Math.sin((i * Math.PI) / 8))
  }));
  const curvas: string[] = [];
  const ilhas: [number, number, number, number][] = [[360, 330, 230, 7], [1180, 620, 280, 8], [900, 180, 120, 4], [240, 800, 150, 5], [1450, 200, 110, 4]];
  for (const [cx, cy, r, n] of ilhas) {
    for (let k = 0; k < n; k++) {
      const rr = r * (1 - k / (n + 0.6));
      const pts: string[] = [];
      for (let i = 0; i < 64; i++) {
        const a = (i / 64) * Math.PI * 2;
        const d = rr * (1 + 0.18 * Math.sin(3 * a + cx) + 0.1 * Math.sin(5 * a + cy) + 0.06 * Math.sin(9 * a + k));
        pts.push(`${Math.round(cx + d * Math.cos(a))},${Math.round(cy + d * 0.72 * Math.sin(a))}`);
      }
      curvas.push(`M${pts.join(' L')}Z`);
    }
  }
  return { grade, rumos, curvas };
}
